import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/core/supabase/client';
import { useAuth } from '@/core/auth/useAuth';
import { useMemo, useCallback } from 'react';

/**
 * Hook to check which app modules the current user can access.
 * Combines role defaults + per-user overrides + CFA service filtering.
 *
 * Usage:
 *   const { hasModule, isLoading } = useModuleAccess();
 *   if (hasModule('laundry')) { ... }
 */
export function useModuleAccess() {
  const { user } = useAuth();

  const { data: modulesList = [], isLoading } = useQuery<string[]>({
    queryKey: ['user-modules', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_user_accessible_modules');
      if (error) throw error;
      return (data as string[]) ?? [];
    },
    enabled: !!user?.id,
    // A revoked module must reach a session already open. Hourly by choice of
    // the client: cheap enough to leave running all day at the counter.
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000, // must stay > staleTime (project rule)
    // The shared client disables refetchOnWindowFocus, and the nav shell never
    // remounts: without these two the revoke would only land on a page change.
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 60_000,
  });

  const modulesSet = useMemo(() => new Set(modulesList), [modulesList]);

  const hasModule = useCallback(
    (code: string) => modulesSet.has(code),
    [modulesSet]
  );

  return { modules: modulesSet, modulesList, hasModule, isLoading };
}
